import {ApiError, IWishlistData, Wishlist, WishlistFactory} from "@connox/typescript-library";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {BrowserStorage} from "../../storage/BrowserStorage";
import {getShopId} from "../../components/lib/getShopId";
import window from "../../components/lib/WindowExtended";
import {RootState} from "../store";
import {Tracking} from "../../tracking/tracking";
import {PossibleNetworkError} from "../../components/cart/ShoppingCart/ShoppingCart";
import * as Sentry from "@sentry/browser";
import {AppDispatch} from "../hooks";

export type WishlistSliceState = {
  wishlist: Wishlist;
  loading: boolean;
  isNotLoggedInModalShown: boolean;
  isWishlistFullModalShown: boolean;
  itemIdToBeAdded?: number;
  itemIdToShowOnPopup: number;
};

const initialState = {} as WishlistSliceState;

export const wishlistStorage = new BrowserStorage<IWishlistData | "empty">({
  type: "sessionStorage",
  dataIdentifier: "wishlist",
  toBeCompressed: true,
  hoursToCache: 1,
});

export const createWishlist = createAsyncThunk<
  Wishlist | null,
  {visitorId: string; itemId?: number},
  {state: RootState; dispatch: AppDispatch}
>("wishlist/create", async ({visitorId, itemId}, {dispatch, getState}) => {
  const wishlistPromise = async () => {
    if (itemId) {
      return await WishlistFactory.addItemToNewWishlist(getShopId(), window.c_ActiveCurrency, visitorId, itemId);
    } else {
      return await WishlistFactory.getWishlist(getShopId(), window.c_ActiveCurrency, visitorId);
    }
  };
  const storedWishlist = wishlistStorage.read(visitorId);
  let wishlist: Wishlist | null = null;

  if (!itemId) {
    if (storedWishlist === "empty") {
      return null;
    }
    if (storedWishlist !== null) {
      wishlist = new Wishlist(getShopId(), window.c_ActiveCurrency, visitorId, storedWishlist);
    }
  }

  if (itemId || !storedWishlist) {
    try {
      wishlist = await wishlistPromise();
    } catch (error) {
      if (itemId) {
        handleError(error, itemId, "add", getState().wishlist.wishlist, dispatch);
      }
    }
  }

  if (wishlist) {
    wishlistStorage.write(visitorId, {id: wishlist.id, items: wishlist.items, modified: wishlist.modified});
    wishlist.emitter.on(() => {
      if (wishlist) {
        wishlistStorage.write(visitorId, {
          id: wishlist.id,
          items: wishlist.items,
          modified: wishlist.modified,
        });
        dispatch(updateWishlistState({wishlist}));
      }
    });
    if (itemId) {
      const addedItem = wishlist.items.find((itm) => itm.item.itemId === itemId);
      if (addedItem) {
        Tracking.sendAddToWishlist(wishlist.items, addedItem);
      }
    }
  } else if (!itemId) {
    wishlistStorage.write(visitorId, "empty");
  }

  return wishlist;
});

export const toggleAddItem = createAsyncThunk<void, number, {state: RootState; dispatch: AppDispatch}>(
  "wishlist/toggleAddItem",
  async (itemId, {getState, dispatch}) => {
    const wishlist = getState().wishlist.wishlist;
    const customer = getState().customer.customer;
    if (!customer.isRecognised) {
      dispatch(updateWishlistState({itemIdToBeAdded: itemId, isNotLoggedInModalShown: true}));

      return;
    }
    if (!wishlist) {
      void dispatch(createWishlist({visitorId: customer.visitorId, itemId})).then(() => {
        dispatch(
          updateWishlistState({
            itemIdToBeAdded: itemId,
            itemIdToShowOnPopup: itemId,
            isNotLoggedInModalShown: false,
          }),
        );
      });

      return;
    }
    const alreadyAddedItem = wishlist.items.find(({item}) => item.itemId === itemId);
    await (alreadyAddedItem ? wishlist.deleteItem(itemId) : wishlist.addItem(itemId))
      .then(() => {
        if (alreadyAddedItem) {
          Tracking.sendRemoveFromWishlist(wishlist.items, alreadyAddedItem);
        } else {
          const {items} = wishlist;
          // get the item of interest after it is added, for exponea
          const addedItem = items.find((itm) => itm.item.itemId === itemId);
          if (addedItem) {
            Tracking.sendAddToWishlist(items, addedItem);
          }
        }
      })
      .catch((error) => handleError(error, itemId, alreadyAddedItem ? "delete" : "add", wishlist, dispatch));
  },
);

const handleError = (error, itemId: number, call: "add" | "delete", wishlist: Wishlist, dispatch: AppDispatch) => {
  const tempError = ((error as ApiError).originalError ?? error) as PossibleNetworkError;
  const isMaxItemsReached = !!tempError?.response?.data.messages?.some((msg) => msg.code === MAX_ITEMS_REACHED);
  if (isMaxItemsReached) {
    dispatch(updateWishlistState({isWishlistFullModalShown: true}));
  } else {
    tempError.name = "Wishlist" + tempError.name;
    Sentry.captureException(tempError, {
      extra: {
        occurence: `PDP wishlist ${call} (Item: ${String(itemId)}, wlId: ${
          wishlist ? wishlist.id : `no_wishlist_on_${call}`
        })`,
      },
    });
  }
};

export const wishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    updateWishlistState: (state: WishlistSliceState, action: PayloadAction<Partial<WishlistSliceState>>) => {
      return {...state, ...action.payload};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createWishlist.pending, (state) => {
        return {...state, loading: true};
      })
      .addCase(createWishlist.fulfilled, (state, action) => {
        const wishlist = action.payload;
        if (wishlist) {
          return {...state, wishlist, loading: false};
        } else {
          return {...state, loading: false};
        }
      })
      .addCase(createWishlist.rejected, (state) => {
        return {...state, loading: false, itemIdToBeAdded: undefined};
      });
  },
});

export const {updateWishlistState} = wishlistSlice.actions;
const MAX_ITEMS_REACHED = "WISHLIST_MAX_ITEMS_REACHED";
